// ── KYB proposed config (v2), sectionized by config file ────────────────────
//
// Mockup only (local state, never written to the workflow JSON). Like KYC v2,
// the workflow engine orchestrates; per-customer you configure the KYB service
// settings + data-source connectors:
//   kyb                — UBO threshold, per-category check types, ABR, aging, reports
//   con_abr            — ABN/ACN validation
//   con_creditorwatch  — AU company data + UBO reports
//   con_kyckr          — international registry lookups
//   risks              — organisation risk model
//   portal_misc        — KYB feature flags
import { useState } from 'react'
import { Checkbox, Field, SectionLabel, TextInput, Toggle } from './fields'
import { ConfigSection } from './ConfigSection'

function KybServiceSection() {
  const [uboThreshold, setUboThreshold] = useState('25')
  const [pseudoUbosOfficers, setPseudoUbosOfficers] = useState(true)
  const [orgCheck, setOrgCheck] = useState('pep_media')
  const [uboCheck, setUboCheck] = useState('two_plus,pep_media')
  const [pseudoUboCheck, setPseudoUboCheck] = useState('two_plus,pep_media')
  const [abrEnabled, setAbrEnabled] = useState(true)
  const [validateAcn, setValidateAcn] = useState(true)
  const [agingAbr, setAgingAbr] = useState('7d')
  const [agingOwnership, setAgingOwnership] = useState('7d')
  const [agingDefault, setAgingDefault] = useState('never')
  const [agingIntl, setAgingIntl] = useState('immediately')
  const [stopOnError, setStopOnError] = useState(false)
  const [uboAlwaysGenerate, setUboAlwaysGenerate] = useState(true)
  const [creditScore, setCreditScore] = useState(true)
  const [creditReport, setCreditReport] = useState(true)
  const [paymentPredictor, setPaymentPredictor] = useState(false)
  return (
    <ConfigSection title="KYB service config" file="kyb" defaultOpen>
      <Field label="Beneficial owner threshold (%)">
        <TextInput type="number" value={uboThreshold} onChange={(e) => setUboThreshold(e.target.value)} placeholder="25" />
      </Field>

      <SectionLabel>Check type by entity category</SectionLabel>
      <Field label="Organisation"><TextInput value={orgCheck} onChange={(e) => setOrgCheck(e.target.value)} placeholder="pep_media" /></Field>
      <Field label="UBOs (individual)"><TextInput value={uboCheck} onChange={(e) => setUboCheck(e.target.value)} placeholder="two_plus,pep_media" /></Field>
      <Field label="Pseudo-UBOs (individual)"><TextInput value={pseudoUboCheck} onChange={(e) => setPseudoUboCheck(e.target.value)} placeholder="two_plus,pep_media" /></Field>
      <Checkbox label="Treat officers/directors as pseudo-UBOs" checked={pseudoUbosOfficers} onChange={setPseudoUbosOfficers} />

      <SectionLabel>ABR</SectionLabel>
      <Toggle label="ABR lookups enabled" checked={abrEnabled} onChange={setAbrEnabled} />
      <Toggle label="Validate ACN" checked={validateAcn} onChange={setValidateAcn} />

      <SectionLabel>Check aging</SectionLabel>
      <Field label="ABR"><TextInput value={agingAbr} onChange={(e) => setAgingAbr(e.target.value)} placeholder="7d" /></Field>
      <Field label="Ownership"><TextInput value={agingOwnership} onChange={(e) => setAgingOwnership(e.target.value)} placeholder="7d" /></Field>
      <Field label="Default"><TextInput value={agingDefault} onChange={(e) => setAgingDefault(e.target.value)} placeholder="never" /></Field>
      <Field label="International"><TextInput value={agingIntl} onChange={(e) => setAgingIntl(e.target.value)} placeholder="immediately" /></Field>

      <SectionLabel>Reports & behaviour</SectionLabel>
      <Toggle label="Stop on error" checked={stopOnError} onChange={setStopOnError} />
      <Toggle label="Always generate UBO report" checked={uboAlwaysGenerate} onChange={setUboAlwaysGenerate} />
      <Checkbox label="Credit score report" checked={creditScore} onChange={setCreditScore} />
      <Checkbox label="Credit report" checked={creditReport} onChange={setCreditReport} />
      <Checkbox label="Payment predictor" checked={paymentPredictor} onChange={setPaymentPredictor} />
    </ConfigSection>
  )
}

function AbrSection() {
  const [endpoint, setEndpoint] = useState('https://abr.business.gov.au/json/')
  const [guid, setGuid] = useState('SECRET:.../abr/guid')
  return (
    <ConfigSection title="ABR connector" file="con_abr">
      <Field label="Endpoint"><TextInput value={endpoint} onChange={(e) => setEndpoint(e.target.value)} /></Field>
      <Field label="GUID (secret path)"><TextInput value={guid} onChange={(e) => setGuid(e.target.value)} placeholder="SECRET:…" /></Field>
    </ConfigSection>
  )
}

function CreditorWatchSection() {
  const [endpoint, setEndpoint] = useState('https://api.creditorwatch.com.au')
  const [username, setUsername] = useState('SECRET:.../creditorwatch/username')
  const [password, setPassword] = useState('SECRET:.../creditorwatch/password')
  const [uboThreshold, setUboThreshold] = useState('10')
  const [anzsic, setAnzsic] = useState(false)
  return (
    <ConfigSection title="CreditorWatch connector (AU)" file="con_creditorwatch">
      <Field label="Endpoint"><TextInput value={endpoint} onChange={(e) => setEndpoint(e.target.value)} /></Field>
      <Field label="Username (secret path)"><TextInput value={username} onChange={(e) => setUsername(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="Password (secret path)"><TextInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="UBO report threshold (%)"><TextInput type="number" value={uboThreshold} onChange={(e) => setUboThreshold(e.target.value)} placeholder="10" /></Field>
      <Toggle label="Include ANZSIC industry codes" checked={anzsic} onChange={setAnzsic} />
    </ConfigSection>
  )
}

function KyckrSection() {
  const [endpoint, setEndpoint] = useState('https://prodws.kyckr.com/GBRDServices.asmx')
  const [restEndpoint, setRestEndpoint] = useState('https://rest.kyckr.com/core/')
  const [email, setEmail] = useState('SECRET:.../kyckr/email')
  const [password, setPassword] = useState('SECRET:.../kyckr/password')
  const [accessToken, setAccessToken] = useState('SECRET:.../kyckr/access_token')
  const [bucket, setBucket] = useState('prod-kyb-service-docs')
  return (
    <ConfigSection title="Kyckr connector (international)" file="con_kyckr">
      <Field label="SOAP endpoint"><TextInput value={endpoint} onChange={(e) => setEndpoint(e.target.value)} /></Field>
      <Field label="REST endpoint"><TextInput value={restEndpoint} onChange={(e) => setRestEndpoint(e.target.value)} /></Field>
      <Field label="Email (secret path)"><TextInput value={email} onChange={(e) => setEmail(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="Password (secret path)"><TextInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="Access token (secret path)"><TextInput value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="S3 bucket"><TextInput value={bucket} onChange={(e) => setBucket(e.target.value)} /></Field>
    </ConfigSection>
  )
}

function OrgRisksSection() {
  const [deregistered, setDeregistered] = useState('50')
  const [sanctions, setSanctions] = useState('80')
  const [adverseMedia, setAdverseMedia] = useState('40')
  const [industry, setIndustry] = useState('20')
  return (
    <ConfigSection title="Organisation risk model" file="risks">
      <Field label="Deregistered company weight"><TextInput type="number" value={deregistered} onChange={(e) => setDeregistered(e.target.value)} /></Field>
      <Field label="Sanctions hit weight"><TextInput type="number" value={sanctions} onChange={(e) => setSanctions(e.target.value)} /></Field>
      <Field label="Adverse media weight"><TextInput type="number" value={adverseMedia} onChange={(e) => setAdverseMedia(e.target.value)} /></Field>
      <Field label="High-risk industry weight"><TextInput type="number" value={industry} onChange={(e) => setIndustry(e.target.value)} /></Field>
    </ConfigSection>
  )
}

function PortalMiscSection() {
  const [kybV2, setKybV2] = useState(true)
  const [internationalKyb, setInternationalKyb] = useState(true)
  return (
    <ConfigSection title="Feature flags" file="portal_misc">
      <Toggle label="KYB v2" checked={kybV2} onChange={setKybV2} />
      <Toggle label="International KYB" checked={internationalKyb} onChange={setInternationalKyb} />
    </ConfigSection>
  )
}

export function KybProposedConfig() {
  return (
    <>
      <KybServiceSection />
      <AbrSection />
      <CreditorWatchSection />
      <KyckrSection />
      <ConfigSection title="AML on UBOs" file="con_complyadvantage">
        <p className="text-[11px] text-[#8fa0bd]">
          UBOs / directors discovered during KYB are AML-screened via the AML connector — configure it on an AML node.
        </p>
      </ConfigSection>
      <OrgRisksSection />
      <PortalMiscSection />
    </>
  )
}
